"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

/** How long a back / forward waits for its page to grow tall enough to reach the place it left. */
const RESTORE_FOR_MS = 2_000;

/** Where each address was left, for back / forward to return to. Keyed by path + query; this tab only. */
const places = new Map<string, number>();
/** Set by back / forward (`popstate`) for the path change it causes. Any other path change is a forward navigation. */
let fromHistory = false;
/** While a back / forward is putting its page back, its scrolling is not «where the page was left». */
let restoring = false;
let watching = false;
/** The path on screen — a back / forward that keeps it (a query-only change) is not a page change. */
let shownPath = "";
const here = () => window.location.pathname + window.location.search;
function watch() {
  if (watching) return;
  watching = true;
  window.addEventListener("popstate", () => {
    if (window.location.pathname === shownPath) return;
    fromHistory = true;
    restoring = true;
  });
  const keep = () => {
    if (!restoring) places.set(here(), window.scrollY);
  };
  window.addEventListener("scroll", keep, {
    passive: true
  });
  // And at the click that leaves the page — before anything else moves — in case no scroll event has reported it yet.
  document.addEventListener("click", keep, true);
}

/** Back to `top` once the page is tall enough, for up to `RESTORE_FOR_MS`; the reader's own scroll ends it. */
function restoreTo(top: number) {
  const until = Date.now() + RESTORE_FOR_MS;
  const stop = () => {
    restoring = false;
    window.removeEventListener("wheel", stop);
    window.removeEventListener("touchstart", stop);
    window.removeEventListener("keydown", stop);
  };
  window.addEventListener("wheel", stop, {
    passive: true
  });
  window.addEventListener("touchstart", stop, {
    passive: true
  });
  window.addEventListener("keydown", stop);
  const step = () => {
    if (!restoring) return;
    window.scrollTo({
      top,
      left: 0,
      behavior: "instant"
    });
    if (Math.abs(window.scrollY - top) <= 2 || Date.now() > until) {
      stop();
      return;
    }
    window.setTimeout(step, 100);
  };
  step();
}

/**
 * A NEW PAGE OPENS AT ITS TOP. Next scrolls a navigation by bringing the new page's first element into view — and
 * when that element has no box (a JSON-LD script) or sits under the fixed header, it stops short, or does not move
 * at all: a collection opened from 1,400px down the list opened 1,400px down. So every forward navigation that
 * changes the path (a link, `router.push`) goes to 0, and back / forward return to where that page was left. A #hash
 * link and a query-only change (a tab, a filter) are left alone. Mounted once beside the shell; renders nothing.
 */
export default function RouteScrollTop() {
  const $ = _c(4);
  const pathname = usePathname();
  const last = useRef(pathname);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp, t0);
  let t1;
  let t2;
  if ($[1] !== pathname) {
    t1 = () => {
      if (pathname === last.current) {
        return;
      }
      last.current = pathname;
      shownPath = window.location.pathname;
      fromHistory = false;
      if (fromHistory) {
        restoreTo(places.get(here()) ?? 0);
        return;
      }
      if (window.location.hash) {
        return;
      }
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant"
      });
    };
    t2 = [pathname];
    $[1] = pathname;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp() {
  shownPath = window.location.pathname;
  watch();
}